import * as types from '@/store/mutation-types'
import { calculAgro, calculEco, createScenario, createSimulation, duplicateScenario, duplicateSimulation, renameSimulation, getExportCSV, loadResultByCulture, loadResultByIlot, loadResultForGraph, loadSimulation, removeScenario, removeSimulation, saveParcelles, updateScenario, updateSynthese } from '@/api/client-api'

const listTypeFiltre = [
  { value: 'all', label: 'Toutes années' },
  { value: 'spring_dry', label: 'Printemps sec', date: '01/03' },
  { value: 'summer_dry', label: 'été sec', date: '01/06' }
]

const ilotNull = {
  id_ilot: null,
  nom_ilot: 'Tous les îlots'
}

const cultureNull = {
  id_culture: null,
  nom_culture: "Toutes les cultures de l'îlot"
}

const state = {
  simulations: [],
  currentSimulation: null,
  anneesSeches: [],
  simuClim: [],
  filtreAnnee: listTypeFiltre[0],
  filtreIlot: ilotNull,
  filtreCulture: cultureNull
}

const mutations = {
  [types.SET_SIMULATIONS] (state, simulations) {
    state.simulations = simulations
  },
  [types.ADD_SIMULATION] (state, simulation) {
    state.simulations.push(simulation)
    state.simulations.sort((a, b) => a.nom.localeCompare(b.nom))
  },
  [types.SET_ANNEES_SECHES] (state, anneesSeches) {
    state.anneesSeches = anneesSeches
  },
  [types.SET_SIMU_CLIM] (state, simuClims) {
    state.simuClim = simuClims
  },
  [types.DEL_SIMULATION] (state, id) {
    state.simulations.splice(state.simulations.findIndex(e => e.id_simulation === id), 1)
  },
  [types.SET_SIMULATION] (state, simulation) {
    state.currentSimulation = simulation
  },
  [types.SET_SIMULATION_PARCELLES] (state, parcelles) {
    state.currentSimulation.parcelles = parcelles
  },
  [types.ADD_SIMULATION_SCENARIO] (state, scenario) {
    state.currentSimulation.scenarios.push(scenario)
  },
  [types.UPDATE_SIMULATION_SCENARIO] (state, scenario) {
    let idx = state.currentSimulation.scenarios.findIndex(e => e.id_scenario === scenario.id_scenario)
    state.currentSimulation.scenarios[idx].nom = scenario.nom
  },
  [types.UPDATE_SIMULATION_NAME] (state, simulation) {
    let idx = state.simulations.findIndex(e => e.id_simulation === simulation.id_simulation)
    state.simulations[idx].nom = simulation.nom
  },
  [types.DEL_SIMULATION_SCENARIO] (state, id) {
    state.currentSimulation.scenarios.splice(state.currentSimulation.scenarios.findIndex(e => e.id_scenario === id), 1)
  },

  [types.SET_FILTRE_ANNEE] (state, id) {
    state.filtreAnnee = id
  },
  [types.SET_FILTRE_ILOT] (state, ilot) {
    state.filtreIlot = ilot == null ? ilotNull : ilot
  },
  [types.SET_FILTRE_CULTURE] (state, culture) {
    state.filtreCulture = culture == null ? cultureNull : culture
  },
  [types.UPDATE_SIMULATION_CLIMATIQUE] (state, { fichier, annee }) {
    state.currentSimulation.simulation.fichier_simu_clim = fichier
    state.currentSimulation.simulation.annee_simu_clim = annee
  },
  [types.UPDATE_SYNTHESE] (state, synthese) {
    state.currentSimulation.simulation.synthese = synthese
  },
  [types.SET_HAS_RESULTAT] (state, hasResultat) {
    state.currentSimulation.has_resultat = hasResultat
  }
}

const actions = {
  loadByIlot: ({ commit, getters }) => {
    commit(types.ADD_LOADING_MESSAGES, 'Chargement des résultats de la simulation')
    return loadResultByIlot(getters.exploitationId, getters.simulationId, getters.selectedIlotId, getters.selectedYears)
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  loadByCulture: ({ commit, getters }) => {
    commit(types.ADD_LOADING_MESSAGES, 'Chargement des résultats de la simulation')
    return loadResultByCulture(getters.exploitationId, getters.simulationId, getters.selectedIlotId, getters.selectedYears)
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  loadResultForGraph: ({ commit, getters }, { type }) => {
    return loadResultForGraph(getters.exploitationId, getters.simulationId, type, getters.selectedIlotId, getters.selectedCultureId, getters.selectedYears)
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  getExportForCSV: ({ commit, getters }, type) => {
    commit(types.ADD_LOADING_MESSAGES, 'Préparation du fichier')
    return getExportCSV(getters.exploitationId, getters.simulationId, getters.selectedIlotId, getters.selectedYears, type)
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  // -------------------------------------------------------------
  createSimulation: ({ commit }, { idExploitation, nom }) => {
    commit(types.ADD_LOADING_MESSAGES, "Création d'une simulation")
    return createSimulation(idExploitation, nom)
      .then(simulation => {
        commit(types.ADD_SIMULATION, simulation)
        return simulation.id_simulation
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  loadSimulation: ({ commit }, { idExploitation, idSimulation }) => {
    commit(types.ADD_LOADING_MESSAGES, "Chargement d'une simulation")
    return loadSimulation(idExploitation, idSimulation)
      .then(data => {
        commit(types.SET_SIMULATION, data)
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  removeSimulation: ({ commit }, { idExploitation, idSimulation }) => {
    commit(types.ADD_LOADING_MESSAGES, "Supression d'une simulation")
    return removeSimulation(idExploitation, idSimulation)
      .then(data => {
        commit(types.DEL_SIMULATION, idSimulation)
        return data
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  duplicateSimulation: ({ commit }, { idExploitation, idSimulation, nom }) => {
    commit(types.ADD_LOADING_MESSAGES, "Duplication d'une simulation")
    return duplicateSimulation(idExploitation, idSimulation, nom)
      .then(simulation => {
        commit(types.ADD_SIMULATION, simulation)
        return simulation.id_simulation
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  renameSimulation: ({ commit }, { idExploitation, idSimulation, nom }) => {
    commit(types.ADD_LOADING_MESSAGES, 'Renommage de la simulation')
    return renameSimulation(idExploitation, idSimulation, nom)
      .then(simulation => {
        commit(types.UPDATE_SIMULATION_NAME, simulation)
        return simulation
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  // -------------------------------------------------------------
  saveParcelles: ({ commit }, { idExploitation, idSimulation, idIlot, parcelles }) => {
    commit(types.ADD_LOADING_MESSAGES, 'Sauvegarde des données de simulation')
    return saveParcelles(idExploitation, idSimulation, idIlot, parcelles)
      .then(data => {
        commit(types.SET_SIMULATION_PARCELLES, data.parcelles)
        commit(types.SET_HAS_RESULTAT, data.has_resultat)
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  createScenario: ({ commit }, { idExploitation, idSimulation, nom }) => {
    commit(types.ADD_LOADING_MESSAGES, "Création d'un scénario")
    return createScenario(idExploitation, idSimulation, nom)
      .then(scenario => {
        commit(types.ADD_SIMULATION_SCENARIO, scenario)
        return scenario
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  updateScenario: ({ commit }, { idExploitation, idSimulation, idScenario, nom }) => {
    commit(types.ADD_LOADING_MESSAGES, "Mise à jour d'un scénario")
    return updateScenario(idExploitation, idSimulation, idScenario, nom)
      .then(scenario => {
        commit(types.UPDATE_SIMULATION_SCENARIO, scenario)
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  duplicateScenario: ({ commit, dispatch }, { idExploitation, idSimulation, idScenario, nom }) => {
    commit(types.ADD_LOADING_MESSAGES, "Duplication d'un scénario")
    return duplicateScenario(idExploitation, idSimulation, idScenario, nom)
      .then(scenario => {
        return scenario.id_scenario
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },
  removeScenario: ({ commit }, { idExploitation, idSimulation, idScenario }) => {
    commit(types.ADD_LOADING_MESSAGES, "Supression d'un scénario")
    return removeScenario(idExploitation, idSimulation, idScenario)
      .then(() => {
        commit(types.DEL_SIMULATION_SCENARIO, idScenario)
      })
      .finally(() => commit(types.DEL_LOADING_MESSAGES))
  },

  calculSimulation: ({ commit, getters, dispatch }) => {
    let p = updateSynthese(getters.exploitationId, getters.simulationId, getters.simulationInfo)
      .then(data => commit(types.UPDATE_SYNTHESE, data))

    getters.scenarios.forEach(scenario => {
      p = p
        .then(() => {
          commit(types.ADD_LOADING_MESSAGES, `Appel agro ${scenario.nom}`)
          return calculAgro(getters.exploitationId, getters.simulationId, scenario.id_scenario)
            .then(() => {
              commit(types.ADD_LOADING_MESSAGES, `Appel eco ${scenario.nom}`)
              return calculEco(getters.exploitationId, getters.simulationId, scenario.id_scenario)
                .then(() => commit(types.SET_HAS_RESULTAT, true))
                .catch(err => {
                  dispatch('alerte/alerte', {
                    title: 'Erreur',
                    message: "Erreur lors de l'appel ECO\n" + err.response.data,
                    okTest: 'Ok'
                  })
                })
            })
            .catch(err => {
              dispatch('alerte/alerte', {
                title: 'Erreur',
                message: "Erreur lors de l'appel AGRO\n" + err.response.data,
                okTest: 'Ok'
              })
            })
        })
    })
    return p
  }
}

const getters = {
  simulations (state) {
    return (state.simulations || [])
      .map(s => ({ ...s }))
      .sort((a, b) => a.nom.localeCompare(b.nom))
  },
  nomSimulation (state) {
    return state.currentSimulation ? state.currentSimulation.simulation.nom : ''
  },
  simulationId (state) {
    return state.currentSimulation ? state.currentSimulation.simulation.id_simulation : ''
  },
  simulationInfo (state) {
    return {
      fichier: state.currentSimulation ? state.currentSimulation.simulation.fichier_simu_clim : null,
      annee: state.currentSimulation ? state.currentSimulation.simulation.annee_simu_clim : null
    }
  },
  selectedYears (state) {
    if (!state.filtreAnnee.date) {
      return []
    }
    return state.anneesSeches
      .filter(a => a.date_debut === state.filtreAnnee.date)
      .sort((a, b) => a.valeur - b.valeur)
      .filter((a, idx) => idx < 4)
      .map(a => a.annee)
  },
  ilots (state, getters) {
    let ilots = [...getters.synthese.ilots || []]
    ilots.unshift(ilotNull)
    return ilots
  },
  synthese (state) {
    if (!state.currentSimulation) {
      return {}
    }
    const synthese = state.currentSimulation.simulation.synthese || { ilots: state.currentSimulation.ilots.map((ilot) => ({ id_ilot: ilot, nom_ilot: ilot })) }
    return typeof synthese === 'string' ? JSON.parse(synthese) : synthese
  },
  cultures (state, getters) {
    let cultures = [...getters.synthese.cultures || []]
    if (getters.selectedIlotId !== null) {
      let idCultures = new Set()
      state.currentSimulation.parcelles.forEach(p => {
        if (p.id_ilot === getters.selectedIlotId) {
          idCultures.add(p.id_culture)
        }
      })
      cultures = cultures.filter(c => idCultures.has(c.id_culture))
    }
    cultures.unshift(cultureNull)
    return cultures
  },
  typeAnnees () {
    return listTypeFiltre
  },
  selectedIlotId (state) {
    return state.filtreIlot.id_ilot
  },
  selectedCultureId (state) {
    return state.filtreCulture.id_culture
  },
  scenarios (state) {
    return (state.currentSimulation || {}).scenarios
  },
  selectedIlotSurface (state, getters) {
    if (state.filtreIlot.id_ilot === null) {
      return getters.synthese.ilots.reduce((acc, ilot) => {
        return acc + (ilot.sau || 0)
      }, 0)
    }
    return state.filtreIlot.sau
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}
